/**
 * Instrument List Tab
 * Search instruments and view which brokers have mapping data (broker_instrument_mappings).
 */

import React, { useState, useCallback } from 'react';
import {
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    TextField,
    InputAdornment,
    Alert,
    CircularProgress,
    Chip,
    Stack,
    IconButton,
    Collapse,
    ToggleButtonGroup,
    ToggleButton,
    Tooltip,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { api } from '@shared/api/client';
import type { InstrumentResponse } from '@shared/api/generated/api';

const MIN_QUERY_LENGTH = 2;
const PAGE_SIZE = 20;

/** Segment filter options – same as instrument sidebar (EQ, FO, CDS, COM). '' = All (no filter). */
const SEGMENT_ALL = '';

/** Extended broker mapping fields returned when include_mappings=true */
interface BrokerMappingRow {
    broker_type?: string;
    broker_instrument_token?: string;
    broker_exchange_token?: string;
    broker_symbol?: string;
    broker_exchange?: string;
    broker_segment?: string;
    broker_metadata?: Record<string, unknown> | null;
    std_exchange?: string;
    std_segment?: string;
    std_instrument_type?: string;
    std_option_type?: string;
}

export function InstrumentListTab() {
    const [query, setQuery] = useState('');
    const [submittedQuery, setSubmittedQuery] = useState('');
    const [instruments, setInstruments] = useState<InstrumentResponse[]>([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [expandedId, setExpandedId] = useState<string | null>(null);
    const [segment, setSegment] = useState<string>(SEGMENT_ALL);

    const runSearch = useCallback(async (searchTerm: string, segmentFilter: string) => {
        if (!searchTerm || searchTerm.trim().length < MIN_QUERY_LENGTH) {
            setInstruments([]);
            setTotal(0);
            setSubmittedQuery('');
            return;
        }
        try {
            setLoading(true);
            setError(null);
            const res = await api.instrumentSearch(
                searchTerm.trim(),
                undefined, // exchange
                segmentFilter || undefined, // segment – filter by selected segment
                undefined, // sector
                undefined, // instrumentType
                true,     // isActive
                PAGE_SIZE,
                0,        // offset
                true      // includeMappings – get broker_mappings from broker_instrument_mappings
            );
            const data = res.data;
            setInstruments(data?.instruments ?? []);
            setTotal(data?.total ?? 0);
            setSubmittedQuery(searchTerm.trim());
        } catch {
            setError('Failed to search instruments. Please try again.');
            setInstruments([]);
            setTotal(0);
        } finally {
            setLoading(false);
        }
    }, []);

    const handleSearch = useCallback(() => {
        runSearch(query, segment);
    }, [query, segment, runSearch]);

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            handleSearch();
        }
    };

    const brokerMappingsList = (inst: InstrumentResponse): string[] => {
        const mappings = inst.broker_mappings;
        if (!mappings || !Array.isArray(mappings)) return [];
        return mappings.map((m: { broker_type?: string }) => m?.broker_type ?? '').filter(Boolean);
    };

    const getMappings = (inst: InstrumentResponse): BrokerMappingRow[] => {
        const mappings = inst.broker_mappings;
        if (!mappings || !Array.isArray(mappings)) return [];
        return mappings as BrokerMappingRow[];
    };

    const formatExpiry = (inst: InstrumentResponse): string => {
        const d = inst.expiry_date;
        if (d == null || d === '') return '—';
        if (typeof d === 'string') return d;
        return String(d);
    };

    const formatMetadata = (m: BrokerMappingRow): string => {
        const meta = m.broker_metadata;
        if (meta == null || typeof meta !== 'object') return '—';
        try {
            const s = JSON.stringify(meta);
            return s.length > 80 ? s.slice(0, 77) + '...' : s;
        } catch {
            return '—';
        }
    };

    return (
        <Box>

            <Box sx={{ mb: 2 }}>
                <Typography variant="caption" sx={{ display: 'block', color: '#04377b', fontWeight: 600, mb: 0.5 }}>
                    Segment
                </Typography>
                <ToggleButtonGroup
                    value={segment}
                    exclusive
                    onChange={(_, v) => v !== null && setSegment(v)}
                    size="small"
                    sx={{
                        backgroundColor: 'rgba(4, 55, 123, 0.05)',
                        border: '1px solid rgba(4, 55, 123, 0.15)',
                        '& .MuiToggleButton-root': {
                            px: 1.5,
                            py: 0.5,
                            fontSize: '0.75rem',
                            fontWeight: 600,
                            color: 'rgba(4, 55, 123, 0.6)',
                            border: 'none',
                            '&.Mui-selected': {
                                backgroundColor: '#04377b',
                                color: '#fff',
                                '&:hover': { backgroundColor: '#032e64' },
                            },
                            '&:hover': { backgroundColor: 'rgba(4, 55, 123, 0.1)' },
                        },
                    }}
                >
                    <ToggleButton value={SEGMENT_ALL}>All</ToggleButton>
                    <Tooltip title="Equities" arrow placement="top">
                        <ToggleButton value="EQ">EQ</ToggleButton>
                    </Tooltip>
                    <Tooltip title="Futures & Options" arrow placement="top">
                        <ToggleButton value="FO">FO</ToggleButton>
                    </Tooltip>
                    <Tooltip title="Currency Derivatives" arrow placement="top">
                        <ToggleButton value="CDS">CDS</ToggleButton>
                    </Tooltip>
                    <Tooltip title="Commodities" arrow placement="top">
                        <ToggleButton value="COM">COM</ToggleButton>
                    </Tooltip>
                </ToggleButtonGroup>
            </Box>

            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                <TextField
                    placeholder="Search by symbol or name (min 2 characters)"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    onKeyDown={handleKeyDown}
                    size="small"
                    sx={{ minWidth: 320 }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon sx={{ color: 'action.active' }} />
                            </InputAdornment>
                        ),
                    }}
                />
                <Typography
                    component="button"
                    onClick={handleSearch}
                    sx={{
                        cursor: 'pointer',
                        px: 2,
                        py: 1,
                        borderRadius: 1,
                        border: '1px solid',
                        borderColor: 'primary.main',
                        color: 'primary.main',
                        fontWeight: 600,
                        fontSize: '0.875rem',
                        bgcolor: 'transparent',
                        '&:hover': { bgcolor: 'rgba(4, 55, 123, 0.04)' },
                    }}
                >
                    Search
                </Typography>
            </Stack>

            {error && (
                <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress size={28} sx={{ color: '#04377b' }} />
                </Box>
            ) : submittedQuery && submittedQuery.length >= MIN_QUERY_LENGTH ? (
                <>
                    <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                        {total} result{total !== 1 ? 's' : ''} for &quot;{submittedQuery}&quot;
                    </Typography>
                    <TableContainer component={Paper} elevation={0} variant="outlined">
                        <Table size="small">
                            <TableHead>
                                <TableRow sx={{ bgcolor: 'rgba(4, 55, 123, 0.04)' }}>
                                    <TableCell sx={{ fontWeight: 600, color: '#04377b', fontSize: '0.75rem', py: 1, width: 40 }} />
                                    {['Symbol', 'Instrument ID', 'Name', 'Exchange', 'Segment', 'Type', 'Expiry date', 'Brokers'].map((h) => (
                                        <TableCell key={h} sx={{ fontWeight: 600, color: '#04377b', fontSize: '0.75rem', py: 1 }}>
                                            {h}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {instruments.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={9} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                                            No instruments found. Try a different search term.
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    instruments.map((inst) => {
                                        const brokers = brokerMappingsList(inst);
                                        const mappings = getMappings(inst);
                                        const isExpanded = expandedId === inst.instrument_id;
                                        return (
                                            <React.Fragment key={inst.instrument_id}>
                                                <TableRow
                                                    key={inst.instrument_id}
                                                    sx={{
                                                        '&:nth-of-type(odd)': { bgcolor: 'rgba(4, 55, 123, 0.015)' },
                                                        '&:hover': { bgcolor: 'rgba(4, 55, 123, 0.03)' },
                                                    }}
                                                >
                                                    <TableCell sx={{ py: 0.5, width: 40 }}>
                                                        {mappings.length > 0 ? (
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => setExpandedId(isExpanded ? null : inst.instrument_id)}
                                                                aria-expanded={isExpanded}
                                                                sx={{ color: '#04377b', p: 0.25 }}
                                                            >
                                                                {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                                                            </IconButton>
                                                        ) : null}
                                                    </TableCell>
                                                    <TableCell sx={{ py: 1, fontWeight: 600, fontSize: '0.8125rem' }}>
                                                        {inst.symbol ?? '—'}
                                                    </TableCell>
                                                    <TableCell sx={{ py: 1, fontSize: '0.7rem', color: 'text.secondary', fontFamily: 'monospace' }}>
                                                        {inst.instrument_id}
                                                    </TableCell>
                                                    <TableCell sx={{ py: 1, fontSize: '0.75rem', maxWidth: 220 }}>
                                                        {inst.name ?? '—'}
                                                    </TableCell>
                                                    <TableCell sx={{ py: 1, fontSize: '0.75rem' }}>{inst.exchange ?? '—'}</TableCell>
                                                    <TableCell sx={{ py: 1, fontSize: '0.75rem', color: 'text.secondary' }}>
                                                        {inst.segment ?? '—'}
                                                    </TableCell>
                                                    <TableCell sx={{ py: 1, fontSize: '0.75rem', color: 'text.secondary' }}>
                                                        {inst.instrument_type ?? '—'}
                                                    </TableCell>
                                                    <TableCell sx={{ py: 1, fontSize: '0.75rem', color: 'text.secondary' }}>
                                                        {formatExpiry(inst)}
                                                    </TableCell>
                                                    <TableCell sx={{ py: 1 }}>
                                                        {brokers.length === 0 ? (
                                                            <Typography variant="caption" color="text.secondary">—</Typography>
                                                        ) : (
                                                            <Stack direction="row" flexWrap="wrap" gap={0.5}>
                                                                {brokers.map((b) => (
                                                                    <Chip
                                                                        key={b}
                                                                        label={b}
                                                                        size="small"
                                                                        sx={{ fontSize: '0.7rem', height: 20 }}
                                                                    />
                                                                ))}
                                                            </Stack>
                                                        )}
                                                    </TableCell>
                                                </TableRow>
                                                {mappings.length > 0 && (
                                                    <TableRow>
                                                        <TableCell colSpan={9} sx={{ py: 0, borderBottom: 'none', verticalAlign: 'top' }}>
                                                            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                                                <Box sx={{ pl: 4, pr: 2, pb: 1 }}>
                                                                    <Typography variant="caption" sx={{ fontWeight: 600, color: '#04377b', display: 'block', mb: 0.5 }}>
                                                                        Broker mapping details
                                                                    </Typography>
                                                                    <Table size="small">
                                                                        <TableHead>
                                                                            <TableRow sx={{ bgcolor: 'rgba(4, 55, 123, 0.06)' }}>
                                                                                {['Broker', 'broker_instrument_token', 'broker_exchange_token', 'broker_symbol', 'broker_exchange', 'broker_segment', 'std_exchange', 'std_segment', 'std_instrument_type', 'std_option_type', 'broker_metadata'].map((h) => (
                                                                                    <TableCell key={h} sx={{ fontWeight: 600, fontSize: '0.7rem', py: 0.5, whiteSpace: 'nowrap' }}>
                                                                                        {h}
                                                                                    </TableCell>
                                                                                ))}
                                                                            </TableRow>
                                                                        </TableHead>
                                                                        <TableBody>
                                                                            {mappings.map((m, idx) => (
                                                                                <TableRow key={idx} sx={{ '&:nth-of-type(even)': { bgcolor: 'rgba(4, 55, 123, 0.02)' } }}>
                                                                                    <TableCell sx={{ fontSize: '0.7rem', py: 0.5 }}>{m.broker_type ?? '—'}</TableCell>
                                                                                    <TableCell sx={{ fontSize: '0.7rem', py: 0.5 }}>{m.broker_instrument_token ?? '—'}</TableCell>
                                                                                    <TableCell sx={{ fontSize: '0.7rem', py: 0.5 }}>{m.broker_exchange_token ?? '—'}</TableCell>
                                                                                    <TableCell sx={{ fontSize: '0.7rem', py: 0.5 }}>{m.broker_symbol ?? '—'}</TableCell>
                                                                                    <TableCell sx={{ fontSize: '0.7rem', py: 0.5 }}>{m.broker_exchange ?? '—'}</TableCell>
                                                                                    <TableCell sx={{ fontSize: '0.7rem', py: 0.5 }}>{m.broker_segment ?? '—'}</TableCell>
                                                                                    <TableCell sx={{ fontSize: '0.7rem', py: 0.5 }}>{m.std_exchange ?? '—'}</TableCell>
                                                                                    <TableCell sx={{ fontSize: '0.7rem', py: 0.5 }}>{m.std_segment ?? '—'}</TableCell>
                                                                                    <TableCell sx={{ fontSize: '0.7rem', py: 0.5 }}>{m.std_instrument_type ?? '—'}</TableCell>
                                                                                    <TableCell sx={{ fontSize: '0.7rem', py: 0.5 }}>{m.std_option_type ?? '—'}</TableCell>
                                                                                    <TableCell sx={{ fontSize: '0.7rem', py: 0.5, maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis' }} title={formatMetadata(m)}>
                                                                                        {formatMetadata(m)}
                                                                                    </TableCell>
                                                                                </TableRow>
                                                                            ))}
                                                                        </TableBody>
                                                                    </Table>
                                                                </Box>
                                                            </Collapse>
                                                        </TableCell>
                                                    </TableRow>
                                                )}
                                            </React.Fragment>
                                        );
                                    })
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </>
            ) : (
                <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                    Enter at least {MIN_QUERY_LENGTH} characters and click Search to see instruments and their broker mappings.
                </Typography>
            )}
        </Box>
    );
}
